/**
 * @Module: Kitchen Context
 * @Status: Ready
 * @Summary: React context for KitchenProfile with Firestore sync + localStorage fallback
 * @Description: KitchenProvider wraps the app and exposes useKitchen(). On mount,
 *   loads from Firestore (if signed in) or localStorage. Updates write to both.
 *   Cross-device sync works via Firestore — same profile on phone and PC.
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useToast } from '@chakra-ui/react';
import type { KitchenProfile } from '../types/kitchen';
import { useAuth } from './AuthContext';
import { isFirebaseConfigured, db } from '../config/firebase';
import { createKitchenService } from '../services/firestore/kitchen.service';
import { AppError } from '../services/firestore/errors';
import { util_log_debug_01 } from '../utils/logging';

const STORAGE_KEY = 'choppy_kitchen_profile';

interface KitchenContextValue {
  profile: KitchenProfile | null;
  loading: boolean;
  updateProfile: (profile: KitchenProfile) => void;
  clearProfile: () => void;
}

const KitchenContext = createContext<KitchenContextValue | undefined>(undefined);

function loadLocal(): KitchenProfile | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) as KitchenProfile : null;
  } catch { return null; }
}

function saveLocal(profile: KitchenProfile | null) {
  try {
    if (profile) localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
    else localStorage.removeItem(STORAGE_KEY);
  } catch { /* quota */ }
}

export const KitchenProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const toast = useToast();
  const [profile, setProfile] = useState<KitchenProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const firestoreDown = useRef(false);
  const serviceRef = useRef(
    isFirebaseConfigured() && db ? createKitchenService(db) : null,
  );
  const uid = user?.uid ?? null;

  // Load on mount + when user changes
  useEffect(() => {
    const load = async () => {
      setLoading(true);

      // Try Firestore first (if signed in)
      if (uid && serviceRef.current && !firestoreDown.current) {
        try {
          const firestoreProfile = await serviceRef.current.get(uid);
          if (firestoreProfile) {
            setProfile(firestoreProfile);
            saveLocal(firestoreProfile); // cache locally too
            util_log_debug_01('KitchenContext: loaded from Firestore');
            setLoading(false);
            return;
          }
        } catch (err) {
          if (err instanceof AppError && err.code === 'SERVICE_UNAVAILABLE') {
            firestoreDown.current = true;
          }
          util_log_debug_01('KitchenContext: Firestore unavailable, falling back');
        }
      }

      // Fall back to localStorage
      const local = loadLocal();
      if (local) {
        setProfile(local);
        util_log_debug_01('KitchenContext: loaded from localStorage');

        // If signed in but Firestore didn't have it, push local → Firestore
        if (uid && serviceRef.current && !firestoreDown.current) {
          try {
            await serviceRef.current.save(uid, local);
            util_log_debug_01('KitchenContext: synced localStorage → Firestore');
          } catch { /* best effort */ }
        }
      }

      setLoading(false);
    };

    load();
  }, [uid]);

  const updateProfile = useCallback((newProfile: KitchenProfile) => {
    setProfile(newProfile);
    saveLocal(newProfile);

    // Sync to Firestore in background
    if (uid && serviceRef.current && !firestoreDown.current) {
      serviceRef.current.save(uid, newProfile).catch((err) => {
        if (err instanceof AppError && err.code === 'SERVICE_UNAVAILABLE') {
          firestoreDown.current = true;
          toast({
            title: 'Saved locally',
            description: 'Kitchen profile saved on this device. Sign in for cloud sync.',
            status: 'warning', duration: 4000, isClosable: true,
          });
        }
      });
    }
  }, [uid, toast]);

  const clearProfile = useCallback(() => {
    setProfile(null);
    saveLocal(null);
    if (uid && serviceRef.current && !firestoreDown.current) {
      serviceRef.current.remove(uid).catch(() => {
        util_log_debug_01('KitchenContext: Firestore delete failed (best effort)');
      });
      util_log_debug_01('KitchenContext: cleared from localStorage + Firestore');
    }
  }, [uid]);

  return (
    <KitchenContext.Provider value={{ profile, loading, updateProfile, clearProfile }}>
      {children}
    </KitchenContext.Provider>
  );
};

export const useKitchen = (): KitchenContextValue => {
  const ctx = useContext(KitchenContext);
  if (!ctx) throw new Error('useKitchen must be used inside <KitchenProvider>');
  return ctx;
};
